import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ROLES } from '@saesak/shared';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/store/auth';
import { registerPushNotifications } from '@/lib/push';

import { LoginPage } from '@/routes/auth/LoginPage';
import { IntroPage } from '@/routes/shared/IntroPage';
import { PrivacyPage } from '@/routes/shared/PrivacyPage';
import { VerifyPage } from '@/routes/shared/VerifyPage';
import { TermsPage } from '@/routes/shared/TermsPage';
import { PricingPage } from '@/routes/shared/PricingPage';
import { RegisterPage } from '@/routes/auth/RegisterPage';
import { InvitePage } from '@/routes/auth/InvitePage';
import { ForgotPasswordPage } from '@/routes/auth/ForgotPasswordPage';
import { OnboardingPage } from '@/routes/auth/OnboardingPage';

import { ParentShell } from '@/routes/parent/ParentShell';
import { ParentHomePage } from '@/routes/parent/ParentHomePage';
import { ScopePage } from '@/routes/parent/ScopePage';

import { StaffShell } from '@/routes/staff/StaffShell';
import { StaffHomePage } from '@/routes/staff/StaffHomePage';
import { DevelopmentTasksPage } from '@/routes/staff/DevelopmentTasksPage';
import { ObservationsPage } from '@/routes/staff/ObservationsPage';
import { CareJournalPage } from '@/routes/staff/CareJournalPage';
import { FormsPage } from '@/routes/staff/FormsPage';
import { DailyNoteEditorPage } from '@/routes/staff/DailyNoteEditorPage';
import { ObservationEditorPage } from '@/routes/staff/ObservationEditorPage';
import { DevelopmentEditorPage } from '@/routes/staff/DevelopmentEditorPage';
import { AttendancePage } from '@/routes/staff/AttendancePage';
import { TalkPage } from '@/routes/staff/TalkPage';
import { OperationsPage } from '@/routes/staff/OperationsPage';
import { AlbumEditorPage } from '@/routes/staff/AlbumEditorPage';
import { NoticeEditorPage } from '@/routes/staff/NoticeEditorPage';
import { EventEditorPage } from '@/routes/staff/EventEditorPage';
import { MealEditorPage } from '@/routes/staff/MealEditorPage';

import { DailyNoteListPage } from '@/routes/shared/DailyNoteListPage';
import { DailyNoteDetailPage } from '@/routes/shared/DailyNoteDetailPage';
import { NoticeListPage } from '@/routes/shared/NoticeListPage';
import { NoticeDetailPage } from '@/routes/shared/NoticeDetailPage';
import { NotificationsPage } from '@/routes/shared/NotificationsPage';
import { MorePage } from '@/routes/shared/MorePage';
import { ChildProfilePage } from '@/routes/shared/ChildProfilePage';
import { LifeRecordsPage } from '@/routes/shared/LifeRecordsPage';
import { NotFoundPage } from '@/routes/shared/NotFoundPage';
import { AlbumListPage, AlbumDetailPage } from '@/routes/shared/AlbumPage';
import { SchedulePage } from '@/routes/shared/SchedulePage';
import { MealPage } from '@/routes/shared/MealPage';
import { MedicationsPage } from '@/routes/shared/MedicationsPage';
import { PickupsPage } from '@/routes/shared/PickupsPage';
import { GrowthPage } from '@/routes/shared/GrowthPage';
import { HealthRecordsPage } from '@/routes/shared/HealthRecordsPage';
import { FeedbackPage } from '@/routes/shared/FeedbackPage';
import { HelpPage } from '@/routes/shared/HelpPage';
import { PoliciesPage } from '@/routes/shared/PoliciesPage';
import { DeleteAccountPage } from '@/routes/shared/DeleteAccountPage';
import { AccountDeletionPage } from '@/routes/shared/AccountDeletionPage';
import { BlocksPage } from '@/routes/shared/BlocksPage';
import {
  SettingsPage,
  TranslationSettingsPage,
  UploadSettingsPage,
} from '@/routes/shared/SettingsPage';
import { CounselingPage } from '@/routes/shared/CounselingPage';
import { MessagesPage, MessageThreadPage } from '@/routes/shared/MessagesPage';
import { CurriculumPage } from '@/routes/shared/CurriculumPage';
import { ChatPage } from '@/routes/parent/ChatPage';
import { RenewalsPage } from '@/routes/parent/RenewalsPage';
import { BusLivePage } from '@/routes/parent/BusLivePage';
import { ConsentsPage } from '@/routes/shared/ConsentsPage';
import { AboutPage } from '@/routes/shared/AboutPage';
import { HandoverPage } from '@/routes/staff/HandoverPage';
import { DocumentsPage } from '@/routes/staff/DocumentsPage';
import { AdminShell } from '@/routes/admin/AdminShell';
import { AdminHomePage } from '@/routes/admin/AdminHomePage';
import { AdminFacilitiesPage } from '@/routes/admin/AdminFacilitiesPage';
import { AdminBillingPage } from '@/routes/admin/AdminBillingPage';
import { AdminErrorsPage } from '@/routes/admin/AdminErrorsPage';
import { AdminSystemPage } from '@/routes/admin/AdminSystemPage';
import { BusPage } from '@/routes/staff/BusPage';
import { BusManagePage } from '@/routes/staff/BusManagePage';
import { PhotoGuidePage } from '@/routes/staff/PhotoGuidePage';
import { WaitlistPage } from '@/routes/staff/WaitlistPage';
import { OpsPage } from '@/routes/staff/OpsPage';
import { PurchasesPage } from '@/routes/staff/PurchasesPage';
import { PurchaseTemplatePage } from '@/routes/staff/PurchaseTemplatePage';

/**
 * 비로그인 첫 화면 (§14.1) — PC 브라우저에는 서비스 소개가 랜딩이고,
 * 폰 브라우저·네이티브 앱은 종전대로 곧장 로그인이다.
 * 딥링크(/notes/... 등)는 화면 크기와 무관하게 로그인으로 보내 from 상태를 지킨다.
 */
function AnonymousHome() {
  const isDesktop = !Capacitor.isNativePlatform() && window.matchMedia('(min-width: 1024px)').matches;
  if (isDesktop) return <IntroPage />;
  return <Navigate to="/login" replace />;
}

/** 폐기된 /records 탭 주소를 새 독립 메뉴로 넘긴다 — 오래된 알림 딥링크 호환 */
function RecordsRedirect() {
  const [params] = useSearchParams();
  const tab = params.get('tab');
  const to = tab === 'observations' ? '/observations' : tab === 'development' ? '/development-tasks' : '/notes';
  return <Navigate to={to} replace />;
}

export function App() {
  const status = useAuth((s) => s.status);
  const bootstrap = useAuth((s) => s.bootstrap);
  const membership = useAuth((s) => s.membership);
  const location = useLocation();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (status === 'authenticated') void registerPushNotifications();
  }, [status]);

  if (status === 'idle' || status === 'loading') {
    return <Spinner label="불러오는 중이에요" />;
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/" element={<AnonymousHome />} />
        <Route path="/intro" element={<IntroPage />} />
        {/* 계정 삭제 공개 안내 — 스토어 데이터 보안 설문의 URL, 비로그인 접근 필수 (§14.1) */}
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location }} />} />
      </Routes>
    );
  }

  // 로그인은 했지만 아직 어느 기관에도 연결되지 않은 상태 — 초대코드 입력으로 유도한다.
  if (!membership) {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    );
  }

  // 시스템 관리자 — 플랫폼 운영 콘솔 (§3.2)
  if (membership.role === ROLES.ADMIN) {
    return (
      <Routes>
        <Route element={<AdminShell />}>
          <Route path="/" element={<AdminHomePage />} />
          <Route path="/facilities" element={<AdminFacilitiesPage />} />
          <Route path="/billing" element={<AdminBillingPage />} />
          <Route path="/errors" element={<AdminErrorsPage />} />
          <Route path="/system" element={<AdminSystemPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  // 차량 기사는 버스 운행 체크 화면에만 접근한다 (§3.2 각주)
  if (membership.role === ROLES.DRIVER) {
    return (
      <Routes>
        <Route path="/" element={<BusPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const isParent = membership.role === ROLES.PARENT;

  return (
    <Routes>
      {/*
       * 모든 화면을 탭 셸 안에 둔다 — 상세·기능 화면에서도 하단 내비가 유지된다.
       * 예외는 입력창이 화면 바닥에 붙어야 하는 대화형 화면(쪽지 스레드·챗봇)뿐이다.
       */}
      <Route element={isParent ? <ParentShell /> : <StaffShell />}>
        {isParent ? (
          <>
            <Route path="/" element={<ParentHomePage />} />
            <Route path="/scope" element={<ScopePage />} />
          </>
        ) : (
          <>
            <Route path="/" element={<StaffHomePage />} />
            <Route path="/talk" element={<TalkPage />} />
          </>
        )}
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/notes" element={<DailyNoteListPage />} />
        <Route path="/album" element={<AlbumListPage />} />
        <Route path="/more" element={<MorePage />} />

        {/* 역할 공통 상세 화면 */}
        <Route path="/notes/:id" element={<DailyNoteDetailPage />} />
        <Route path="/notices" element={<NoticeListPage />} />
        <Route path="/notices/:id" element={<NoticeDetailPage />} />
        <Route path="/children/:id" element={<ChildProfilePage />} />
        <Route path="/life-records" element={<LifeRecordsPage />} />

        {/* P2 일상 기능 (§5.5~§5.11) */}
        <Route path="/album/:id" element={<AlbumDetailPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/meals" element={<MealPage />} />
        <Route path="/medications" element={<MedicationsPage />} />
        <Route path="/pickups" element={<PickupsPage />} />
        <Route path="/growth" element={<GrowthPage />} />
        <Route path="/health-records" element={<HealthRecordsPage />} />
        <Route path="/counseling" element={<CounselingPage />} />
        <Route path="/curriculum" element={<CurriculumPage />} />
        <Route path="/consents" element={<ConsentsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        {/* 앱 설정 채널 (v1.18) — 설정·건의함·도움말·정책 */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/uploads" element={<UploadSettingsPage />} />
        <Route path="/settings/translation" element={<TranslationSettingsPage />} />
        <Route path="/settings/feedback" element={<FeedbackPage />} />
        <Route path="/settings/delete-account" element={<DeleteAccountPage />} />
        {/* 안전 도구 (§5.30) — 차단한 사용자 관리 */}
        <Route path="/settings/blocks" element={<BlocksPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        {isParent && <Route path="/renewals" element={<RenewalsPage />} />}
        {isParent && <Route path="/bus" element={<BusLivePage />} />}

        {/* 교직원 전용 */}
        {!isParent && (
          <>
            {/* "우리 반 기록" 탭 화면은 폐기(v1.18) — 발달기록·관찰메모가 독립 메뉴가 됐다.
                저장된 알림 딥링크(/records?tab=...)는 새 화면으로 넘긴다. */}
            <Route path="/records" element={<RecordsRedirect />} />
            <Route path="/development-tasks" element={<DevelopmentTasksPage />} />
            <Route path="/observations" element={<ObservationsPage />} />
            {/* 보육일지·양식 자료실 (v1.19, §5.14) */}
            <Route path="/care-journal" element={<CareJournalPage />} />
            <Route path="/forms" element={<FormsPage />} />
            <Route path="/records/notes/new" element={<DailyNoteEditorPage />} />
            <Route path="/records/notes/:id/edit" element={<DailyNoteEditorPage />} />
            <Route path="/records/observations/new" element={<ObservationEditorPage />} />
            <Route path="/records/development/:childId" element={<DevelopmentEditorPage />} />
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/operations" element={<OperationsPage />} />
            <Route path="/records/albums/new" element={<AlbumEditorPage />} />
            <Route path="/records/notices/new" element={<NoticeEditorPage />} />
            <Route path="/records/events/new" element={<EventEditorPage />} />
            <Route path="/records/meals/edit" element={<MealEditorPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/photo-guide" element={<PhotoGuidePage />} />
            <Route path="/bus" element={<BusPage />} />
            <Route path="/bus/manage" element={<BusManagePage />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/ops" element={<OpsPage />} />
            {/* 구매대장 (v1.20, §5.29) — 증빙 업로드 → AI 초안 → 대장, 양식은 원장 설정 */}
            <Route path="/purchases" element={<PurchasesPage />} />
            <Route path="/purchases/template" element={<PurchaseTemplatePage />} />
            <Route path="/handover" element={<HandoverPage />} />
          </>
        )}

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* 대화형 전체 화면 — 자체 스크롤과 바닥 입력창을 가진다 */}
      <Route path="/messages/:id" element={<MessageThreadPage />} />
      {isParent && <Route path="/chat" element={<ChatPage />} />}

      {/* 서비스 소개(PC 랜딩) — 로그인 상태에서도 열람 가능, 셸 밖 전체 화면 */}
      <Route path="/intro" element={<IntroPage />} />

      <Route path="/login" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
